/**
 * Compatibility shim that maps react-router-dom-style imports to TanStack Router.
 * Lets us port source components verbatim while we incrementally lift SEO/SSR
 * into TanStack route `head()` definitions.
 */

import {
  Navigate as TNavigate,
  Outlet,
  useLocation as tUseLocation,
  useNavigate as tUseNavigate,
  useParams as tUseParams,
  useSearch as tUseSearch,
} from "@tanstack/react-router";
import {
  forwardRef,
  type AnchorHTMLAttributes,
  type ReactNode,
  useCallback,
  useMemo,
} from "react";

type AnyTo = string;

interface RDLinkProps
  extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href"> {
  to: AnyTo;
  replace?: boolean;
  state?: unknown;
  children?: ReactNode;
}

/**
 * react-router-dom <Link>-compatible wrapper.
 *
 * The public marketing site intentionally uses real document navigations for
 * internal links. CallRail/Cognos/GHL mutate phone links and text nodes after
 * hydration; SPA route transitions then ask React to remove DOM nodes that no
 * longer match the virtual tree, which makes the header disappear on live link
 * clicks. A normal anchor click unloads the old document instead of reconciling
 * mutated DOM, so crawlers and users both get a stable page.
 */
export const Link = forwardRef<HTMLAnchorElement, RDLinkProps>(function Link(
  { to, replace, state: _state, children, ...rest },
  ref,
) {
  return (
    <a ref={ref} href={to} data-replace={replace ? "true" : undefined} {...rest}>
      {children}
    </a>
  );
});

interface RDNavLinkRenderProps {
  isActive: boolean;
  isPending: boolean;
}

interface RDNavLinkProps
  extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href" | "className" | "style" | "children"> {
  to: AnyTo;
  end?: boolean;
  replace?: boolean;
  className?: string | ((p: RDNavLinkRenderProps) => string);
  style?: React.CSSProperties | ((p: RDNavLinkRenderProps) => React.CSSProperties);
  children?: ReactNode | ((p: RDNavLinkRenderProps) => ReactNode);
}

export type NavLinkProps = RDNavLinkProps;
export type LinkProps = RDLinkProps;

export const NavLink = forwardRef<HTMLAnchorElement, RDNavLinkProps>(function NavLink(
  { to, end, replace, className, style, children, ...rest },
  ref,
) {
  const location = tUseLocation();
  const pathname = location.pathname;
  const isActive = end ? pathname === to : pathname === to || pathname.startsWith(to + "/");
  const renderProps: RDNavLinkRenderProps = { isActive, isPending: false };
  const resolvedClass =
    typeof className === "function" ? className(renderProps) : className;
  const resolvedStyle =
    typeof style === "function" ? style(renderProps) : style;
  const resolvedChildren =
    typeof children === "function" ? children(renderProps) : children;
  return (
    <Link ref={ref} to={to} replace={replace} className={resolvedClass} style={resolvedStyle} {...rest}>
      {resolvedChildren}
    </Link>
  );
});

export { Outlet };

interface NavigateOptions {
  replace?: boolean;
  state?: unknown;
}

export function Navigate({ to, replace }: { to: string; replace?: boolean }) {
  return <TNavigate to={to as never} replace={replace} />;
}

/** react-router-dom-compatible useNavigate that accepts a string or `-1`. */
export function useNavigate() {
  const nav = tUseNavigate();
  return useCallback(
    (to: string | number, opts?: NavigateOptions) => {
      if (typeof to === "number") {
        if (typeof window !== "undefined") window.history.go(to);
        return;
      }
      if (typeof window !== "undefined" && /^(\/|\.\/|\.\.\/|https?:\/\/)/.test(to)) {
        if (opts?.replace) window.location.replace(to);
        else window.location.assign(to);
        return;
      }
      void nav({ to: to as never, replace: opts?.replace });
    },
    [nav],
  );
}

export function useLocation() {
  const loc = tUseLocation();
  return {
    pathname: loc.pathname,
    search: loc.searchStr ?? "",
    hash: loc.hash ?? "",
    state: undefined as unknown,
    key: "default",
  };
}

export function useParams<T extends Record<string, string | undefined> = Record<string, string | undefined>>(): T {
  return tUseParams({ strict: false } as any) as T;
}

export function useSearchParams(): [URLSearchParams, (next: URLSearchParams | Record<string, string>) => void] {
  const search = tUseSearch({ strict: false }) as Record<string, unknown>;
  const nav = tUseNavigate();
  const params = useMemo(() => {
    const sp = new URLSearchParams();
    for (const [k, v] of Object.entries(search ?? {})) {
      if (v === undefined || v === null) continue;
      sp.set(k, String(v));
    }
    return sp;
  }, [search]);
  const setParams = useCallback(
    (next: URLSearchParams | Record<string, string>) => {
      const obj: Record<string, string> = {};
      if (next instanceof URLSearchParams) {
        next.forEach((v, k) => {
          obj[k] = v;
        });
      } else {
        Object.assign(obj, next);
      }
      void nav({ to: ".", search: obj as never, replace: true });
    },
    [nav],
  );
  return [params, setParams];
}

/**
 * No-op BrowserRouter / Routes / Route exports so legacy App.tsx-style trees
 * compile (they are not used at runtime — file-based routing is wired in
 * `src/routes/`).
 */
export function BrowserRouter({ children }: { children: ReactNode }) {
  return <>{children}</>;
}
export function Routes({ children }: { children: ReactNode }) {
  return <>{children}</>;
}
export function Route(_props: { path?: string; element?: ReactNode }) {
  return null;
}
